import { localize } from "@gtmhub/localization";
import {
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridFilter,
  IGridFilterDefinition,
  IGridSortingDefinition,
  IGridSortingRule,
  OrFiltersGroup,
  toGridDefinitionMap,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { IGHGridColumn } from "@gtmhub/shared/components/grid/grid.component";

const defaultColWidth = {
  large: 500,
};

const minColWidth = {
  large: 250,
};

const maxColWidth = {
  large: 750,
};

export const OKR_VIEWS_LIST_SCREEN_PATH = ["okr-views-list"];
export const OKR_VIEWS_LIST_SETTING_KEY = "okrViewsListGridColumns";
export const OKR_VIEWS_LIST_FILTERS_KEY = "okrViewsListFilters";
export const OKR_VIEWS_LIST_SORTING_KEY = "okrViewsListSortingRules";

export const defaultGHGridColumns = (): (IGHGridColumn & { isSortable?: boolean })[] => [
  {
    fieldName: "name",
    width: defaultColWidth.large,
    minWidth: minColWidth.large,
    maxWidth: maxColWidth.large,
    type: "alphabetic",
    isCustom: false,
    visible: true,
    label: localize("name"),
    isSortable: true,
  },
  {
    fieldName: "owner",
    width: defaultColWidth.large,
    minWidth: minColWidth.large,
    maxWidth: maxColWidth.large,
    type: "user",
    isCustom: false,
    visible: true,
    label: localize("owner"),
    isSortable: false,
  },
  {
    fieldName: "access",
    width: defaultColWidth.large,
    minWidth: minColWidth.large,
    maxWidth: maxColWidth.large,
    type: "permissions",
    isCustom: false,
    visible: true,
    label: localize("who_has_access"),
    isSortable: false,
  },
];

export const generateDefaultSortingRules = (): IGridSortingRule[] => {
  const sortingRules: IGridSortingRule[] = [];

  const nameSorting: IGridSortingRule = {
    fieldName: "name",
    reversed: false,
  };
  sortingRules.push(nameSorting);

  return sortingRules;
};

export const generateDefaultFilters = (): OrFiltersGroup[] => {
  const filtersGroup: OrFiltersGroup = [];

  const itemFilter: IGridFilter = {
    fieldName: "name",
    dataType: "text",
    operator: "contains",
    value: "",
  };
  filtersGroup.push(itemFilter);

  const ownerFilter: IGridFilter = {
    fieldName: "ownerId",
    dataType: "array",
    operator: "isOneOf",
    value: [],
  };
  filtersGroup.push(ownerFilter);

  const accessFilter: IGridFilter = {
    fieldName: "access",
    dataType: "array",
    operator: "isOneOf",
    value: [],
  };
  filtersGroup.push(accessFilter);

  return [filtersGroup];
};

export const okrViewsSortingDefinitions = (): GridSortingDefinitions => {
  const baseSortingDefinitions: IGridSortingDefinition[] = [];

  baseSortingDefinitions.push({
    fieldName: "name",
    displayName: localize("name"),
  });

  baseSortingDefinitions.push({
    fieldName: "accessType",
    displayName: localize("access"),
  });

  return toGridDefinitionMap([...baseSortingDefinitions]);
};

export const okrViewsFilterDefinitions = (): GridFilterDefinitions => {
  const baseDefinitions: IGridFilterDefinition[] = [];

  baseDefinitions.push({
    fieldName: "name",
    displayName: localize("name"),
    trackingName: "Item",
    dataType: "text",
    defaultOperator: "contains",
    supportedOperators: ["contains"],
    placeholder: localize("search_for_name_of_okr_view"),
  });

  baseDefinitions.push({
    fieldName: "ownerId",
    displayName: localize("owner_cap"),
    trackingName: "Owner",
    dataType: "array",
    arrayType: "user",
    placeholder: localize("search_for_user"),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
  });

  baseDefinitions.push({
    fieldName: "accessType",
    displayName: localize("access"),
    trackingName: "AccessType",
    dataType: "array",
    arrayType: "static",
    placeholder: localize("search_for_access_type"),
    defaultOperator: "isOneOf",
    supportedOperators: ["isOneOf"],
    entries: [
      {
        displayName: localize("everyone_can_edit"),
        value: "everyoneCanEdit",
      },
      {
        displayName: localize("everyone_can_view"),
        value: "everyoneCanView",
      },
      {
        displayName: localize("private_access_only_invited"),
        value: "invitedOnly",
      },
      {
        displayName: localize("private_access_only_you"),
        value: "ownerOnly",
      },
    ],
  });

  return toGridDefinitionMap([...baseDefinitions]);
};
